// /settings/sidebar — Sidebar Module Preferences
//
// Per-user, per-browser preference for which top-level sidebar modules are
// visible. Hidden ≠ disabled: the routes still work and the user can still
// type the URL directly — this is purely about reducing nav clutter.

import { useMemo } from 'react';
import { PageContainer, Button, Toggle } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import {
  getEligibleTopLevelSidebarItems,
  type AdminSidebarItem,
} from '@/router';
import {
  useSidebarPrefsStore,
  NON_HIDEABLE_SIDEBAR_IDS,
  type StartPage,
} from '@/stores/use-sidebar-prefs-store';
import { ModuleIcon, moduleGradient, type ModuleId } from '@/constants/module-icons';

const PLATFORM_ROLES = new Set(['SUPER_ADMIN', 'ADMIN', 'MODERATOR']);
const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100,
  ADMIN: 80,
  MODERATOR: 60,
  OWNER: 50,
  MANAGER: 30,
  CASHIER: 5,
};

export function SidebarPreferencesPage() {
  const profile = useAuthStore((s) => s.profile);
  const flags = useFeatureFlag();
  const hiddenIds = useSidebarPrefsStore((s) => s.hiddenIds);
  const toggle = useSidebarPrefsStore((s) => s.toggle);
  const setHidden = useSidebarPrefsStore((s) => s.setHidden);
  const reset = useSidebarPrefsStore((s) => s.reset);
  const startPage = useSidebarPrefsStore((s) => s.startPage);
  const setStartPage = useSidebarPrefsStore((s) => s.setStartPage);

  const isPlatformUser = PLATFORM_ROLES.has(profile?.role ?? '');
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;

  const eligible: AdminSidebarItem[] = useMemo(
    () => getEligibleTopLevelSidebarItems({ isPlatformUser, roleLevel, flags }),
    [isPlatformUser, roleLevel, flags],
  );

  // L06: the dashboard opens for MANAGER and up. Below that the Dashboard start
  // page is not offered, and Launchpad shows as chosen whatever was saved.
  const canOpenDashboard = eligible.some((it) => it.id === 'dashboard');
  const effectiveStartPage: StartPage = canOpenDashboard ? startPage : 'home';

  const hiddenSet = useMemo(() => new Set(hiddenIds), [hiddenIds]);
  const hideableItems = eligible.filter((it) => !NON_HIDEABLE_SIDEBAR_IDS.has(it.id));
  const visibleCount = eligible.length - hideableItems.filter((it) => hiddenSet.has(it.id)).length;

  const hideAllOptional = () => {
    setHidden(hideableItems.map((it) => it.id));
  };
  const showAll = () => reset();

  return (
    <PageContainer>
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-600 to-indigo-600 rounded-xl p-6 text-white shadow-lg mb-6 animate-fade-in">
        <h1 className="text-2xl font-bold text-white">Sidebar Modules</h1>
        <p className="mt-1 text-sm text-white/85">
          Choose which modules appear in your sidebar. Hidden modules stay
          accessible by URL — this only reduces clutter.
        </p>
      </div>

      {/* KPI summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 p-5 border border-neutral-100 dark:border-neutral-700">
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Visible modules</p>
          <p className="text-2xl font-bold text-neutral-900 dark:text-white tabular-nums mt-1">
            {visibleCount}
            <span className="text-base font-medium text-neutral-400"> / {eligible.length}</span>
          </p>
        </div>
        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 p-5 border border-neutral-100 dark:border-neutral-700">
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Hidden modules</p>
          <p className="text-2xl font-bold text-warning-600 tabular-nums mt-1">
            {hideableItems.filter((it) => hiddenSet.has(it.id)).length}
          </p>
        </div>
        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 p-5 border border-neutral-100 dark:border-neutral-700">
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Always shown</p>
          <p className="text-2xl font-bold text-success-600 tabular-nums mt-1">
            {NON_HIDEABLE_SIDEBAR_IDS.size}
          </p>
        </div>
      </div>

      {/* Start page preference */}
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-md border border-neutral-100 dark:border-neutral-700 p-5 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-semibold text-neutral-900 dark:text-white">
            Start page after login
          </span>
        </div>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
          Choose what you land on when you sign in.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <StartPageOption
            value="home"
            checked={effectiveStartPage === 'home'}
            onSelect={setStartPage}
            title="Launchpad"
            subtitle="Tile grid of your visible modules"
          />
          {canOpenDashboard && (
            <StartPageOption
              value="dashboard"
              checked={effectiveStartPage === 'dashboard'}
              onSelect={setStartPage}
              title="Dashboard"
              subtitle="KPIs, sales wave, analytics"
            />
          )}
        </div>
      </div>

      {/* Bulk actions */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button variant="secondary" onClick={showAll}>Show all</Button>
        <Button variant="secondary" onClick={hideAllOptional}>Hide all optional</Button>
      </div>

      {/* Module list */}
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-md border border-neutral-100 dark:border-neutral-700 divide-y divide-neutral-100 dark:divide-neutral-700">
        {eligible.map((it) => {
          const locked = NON_HIDEABLE_SIDEBAR_IDS.has(it.id);
          const visible = locked || !hiddenSet.has(it.id);
          return (
            <div
              key={it.id}
              className="flex items-center justify-between px-5 py-3 hover:bg-brand-50 dark:hover:bg-neutral-700/40 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={
                    'w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0 bg-gradient-to-br ' +
                    moduleGradient(it.id)
                  }
                >
                  <ModuleIcon id={it.id as ModuleId} className="h-6 w-6" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-neutral-900 dark:text-white truncate">{it.label}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{it.path}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {locked && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-300">
                    Always visible
                  </span>
                )}
                <Toggle
                  checked={visible}
                  onChange={() => toggle(it.id)}
                  disabled={locked}
                />
              </div>
            </div>
          );
        })}
      </div>
    </PageContainer>
  );
}

function StartPageOption({
  value,
  checked,
  onSelect,
  title,
  subtitle,
}: {
  value: StartPage;
  checked: boolean;
  onSelect: (v: StartPage) => void;
  title: string;
  subtitle: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={
        'text-left rounded-lg border p-3 transition-all duration-200 ' +
        (checked
          ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20 ring-2 ring-brand-200 dark:ring-brand-800'
          : 'border-neutral-200 dark:border-neutral-700 hover:border-brand-300 hover:bg-brand-50/40 dark:hover:bg-neutral-700/40')
      }
    >
      <div className="flex items-center justify-between">
        <span className="font-medium text-neutral-900 dark:text-white">{title}</span>
        <span
          className={
            'w-4 h-4 rounded-full border-2 flex items-center justify-center ' +
            (checked ? 'border-brand-500' : 'border-neutral-300 dark:border-neutral-500')
          }
        >
          {checked && <span className="w-2 h-2 rounded-full bg-brand-500" />}
        </span>
      </div>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{subtitle}</p>
    </button>
  );
}
